/**
 * ClubRewards — katalog nagród (publiczny dla zalogowanych).
 * Member klika nagrodę → potwierdza wymianę → dostaje kupon.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clubApi, formatApiErrorDetail } from './clubApi';
import { useClubAuth } from './ClubApp';

const TYPE_LABEL = {
  freebie: 'Gratis',
  percent: 'Rabat %',
  amount: 'Rabat kwotowy',
  drink: 'Napój',
};

export default function ClubRewards() {
  const navigate = useNavigate();
  const { member, refresh } = useClubAuth();
  const [rewards, setRewards] = useState(null);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(null); // reward obj
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // coupon obj po sukcesie

  const load = async () => {
    try {
      const { data } = await clubApi.get('/rewards');
      setRewards(data.rewards || []);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const points = member?.points_total ?? 0;

  const redeem = async () => {
    if (!confirming) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await clubApi.post(`/rewards/${confirming.id}/redeem`);
      setResult(data.coupon);
      setConfirming(null);
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return <RedeemSuccess coupon={result} onClose={() => { setResult(null); navigate('/mrjerryclub/kupony'); }} />;
  }

  return (
    <div className="club-shell">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--club-text)' }}>🎁 Nagrody</div>
          <div style={{ fontSize: 12, color: 'var(--club-text-muted)' }}>Wymień punkty na coś dobrego</div>
        </div>
        <div style={{
          marginLeft: 'auto', padding: '6px 12px', borderRadius: 999,
          background: 'rgba(212,175,55,0.10)', border: '1px solid var(--club-border)',
          fontSize: 13, fontWeight: 800, color: 'var(--club-gold)',
        }} data-testid="club-rewards-balance">
          {points.toLocaleString('pl-PL')} pkt
        </div>
      </div>

      {error && <div className="club-alert club-alert-error">⚠️ {error}</div>}

      {rewards === null ? (
        <div className="club-loading"><div className="club-loading-spinner" /></div>
      ) : rewards.length === 0 ? (
        <div className="club-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🎁</div>
          <div style={{ fontSize: 14, color: 'var(--club-text)' }}>
            Aktualnie brak dostępnych nagród. Wkrótce dodamy świetne propozycje!
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} data-testid="club-rewards-list">
          {rewards.map((r) => {
            const canAfford = points >= r.cost;
            return (
              <button
                key={r.id}
                onClick={() => canAfford && setConfirming(r)}
                disabled={!canAfford}
                data-testid={`club-reward-${r.id}`}
                style={{
                  textAlign: 'left',
                  padding: 0,
                  background: 'var(--club-card)',
                  border: `1px solid ${canAfford ? 'var(--club-border)' : 'var(--club-border-soft)'}`,
                  borderRadius: 14,
                  overflow: 'hidden',
                  cursor: canAfford ? 'pointer' : 'not-allowed',
                  opacity: canAfford ? 1 : 0.55,
                  transition: 'transform 0.2s',
                  fontFamily: 'inherit',
                  color: 'var(--club-text)',
                }}
              >
                <div style={{
                  height: 100,
                  background: r.photo
                    ? `url(${r.photo}) center/cover`
                    : 'linear-gradient(135deg, rgba(212,175,55,0.18), rgba(212,175,55,0.05))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 38,
                }}>
                  {!r.photo && '🎁'}
                </div>
                <div style={{ padding: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, lineHeight: 1.2, marginBottom: 4 }}>{r.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--club-text-muted)', minHeight: 28 }}>
                    {(r.description || '').slice(0, 60)}{(r.description || '').length > 60 ? '…' : ''}
                  </div>
                  <div style={{
                    marginTop: 8, fontSize: 14, fontWeight: 900,
                    color: canAfford ? 'var(--club-gold)' : 'var(--club-text-muted)',
                  }}>
                    {r.cost.toLocaleString('pl-PL')} pkt
                  </div>
                  {!canAfford && (
                    <div style={{ fontSize: 10, color: 'var(--club-text-muted)', marginTop: 2 }}>
                      brak {(r.cost - points).toLocaleString('pl-PL')} pkt
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {confirming && (
        <ConfirmModal
          reward={confirming}
          points={points}
          busy={busy}
          onCancel={() => setConfirming(null)}
          onConfirm={redeem}
        />
      )}
    </div>
  );
}

function ConfirmModal({ reward, points, busy, onCancel, onConfirm }) {
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 200,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0,
      }}
      data-testid="club-redeem-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480, background: 'var(--club-bg-elev)',
          borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24,
          paddingBottom: 'calc(24px + env(safe-area-inset-bottom))',
          borderTop: '1px solid var(--club-border)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🎁</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--club-text)' }}>{reward.name}</div>
          <div style={{ fontSize: 12, color: 'var(--club-text-muted)', marginTop: 4, lineHeight: 1.4 }}>
            {reward.description}
          </div>
        </div>
        <div style={{
          padding: 14, borderRadius: 12,
          background: 'rgba(212,175,55,0.08)', border: '1px solid var(--club-border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16,
        }}>
          <span style={{ fontSize: 13, color: 'var(--club-text-muted)' }}>Koszt nagrody</span>
          <span style={{ fontSize: 22, fontWeight: 900, color: 'var(--club-gold)' }}>{reward.cost} pkt</span>
        </div>
        <div style={{
          padding: 12, borderRadius: 10, background: 'rgba(0,0,0,0.3)',
          fontSize: 12, color: 'var(--club-text-muted)', marginBottom: 16,
        }}>
          Po wymianie:
          <span style={{ color: 'var(--club-text)', marginLeft: 6, fontWeight: 700 }}>
            {(points - reward.cost).toLocaleString('pl-PL')} pkt
          </span>
          <br />
          Kupon ważny: <b style={{ color: 'var(--club-text)' }}>{reward.expiry_days} dni</b>
        </div>
        <button
          className="club-btn club-btn-primary"
          onClick={onConfirm}
          disabled={busy}
          data-testid="club-redeem-confirm"
        >{busy ? 'Wymieniam…' : '🎟️ Tak, wymieniam punkty'}</button>
        <button
          className="club-btn club-btn-ghost"
          onClick={onCancel}
          style={{ marginTop: 8 }}
          disabled={busy}
        >Anuluj</button>
      </div>
    </div>
  );
}

function RedeemSuccess({ coupon, onClose }) {
  return (
    <div className="club-shell">
      <div className="club-brand">
        <div className="club-brand-logo" style={{ background: 'linear-gradient(135deg, #10b981, #047857)' }}>🎉</div>
        <h1 className="club-brand-name" style={{ color: '#10b981' }}>Gratulacje!</h1>
        <div className="club-brand-sub">Twój kupon jest gotowy</div>
      </div>
      <div className="club-card gold" data-testid="club-redeem-success">
        <div style={{ textAlign: 'center', fontSize: 14, marginBottom: 14 }}>{coupon.reward_name}</div>
        <div style={{
          padding: 14, background: 'rgba(0,0,0,0.4)', border: '2px dashed var(--club-gold)',
          borderRadius: 12, textAlign: 'center', fontFamily: 'monospace',
          fontSize: 22, fontWeight: 900, letterSpacing: 2, color: 'var(--club-gold)',
        }} data-testid="club-redeem-code">{coupon.code}</div>
        <div style={{ fontSize: 12, color: 'var(--club-text-muted)', textAlign: 'center', marginTop: 12 }}>
          Pokaż kod obsłudze przy następnej wizycie.
          <br />Kupon ważny do {new Date(coupon.expires_at).toLocaleDateString('pl-PL')}.
        </div>
      </div>
      <button className="club-btn club-btn-primary" onClick={onClose} data-testid="club-redeem-success-close">
        📋 Zobacz moje kupony
      </button>
    </div>
  );
}
